import type { ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';

/**
 * Shared presentation pieces for the ledger admin show pages (M08 design
 * polish). Mirrors the house detail-page vocabulary established by
 * EmployeeDetail (gradient hero) and TransactionShow (stat cards +
 * section papers).
 *
 * @see services/web-ui/src/resources/hr/employee/EmployeeDetail.tsx
 * @see services/web-ui/src/ledger/transaction/TransactionShow.tsx
 * @see sprints/2026-10/Milestone_08_ServiceCatalogueAdminUI/README.md
 */

/**
 * Gradient hero banner: large icon, primary title, subtitle line, chip row,
 * and a right-aligned actions slot.
 */
export function ShowHero({
  icon,
  title,
  subtitle,
  chips,
  actions,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  chips?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        background: 'linear-gradient(135deg, #1565c0, #1976d2)',
        color: 'primary.contrastText',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box sx={{ fontSize: 40, display: 'flex', opacity: 0.9 }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{ opacity: 0.85, overflowWrap: 'anywhere' }}
              >
                {subtitle}
              </Typography>
            )}
            {chips && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {chips}
              </Box>
            )}
          </Box>
        </Box>
        {actions && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

/**
 * Compact statistic tile: caption label above a value slot. Use for the
 * 4-up summary row above the detail cards.
 */
export function StatCard({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{children}</Box>
    </Paper>
  );
}

/**
 * Detail card: icon + bold section heading over a stacked field list.
 * Field elements (react-admin `TextField`/`FunctionField`) render only their
 * value — labels come from `SimpleShowLayout`'s `Labeled` wrapper, which is
 * absent in custom layouts. Pair every field with an explicit `FieldPair`
 * caption instead.
 */
export function SectionPaper({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gap: 2 }}>{children}</Box>
    </Paper>
  );
}

/**
 * Caption label over a react-admin field value, replacing the `Labeled`
 * wrapper that only `SimpleShowLayout`/`TabbedShowLayout` provide.
 */
export function FieldPair({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.25 }}
      >
        {label}
      </Typography>
      <Box sx={{ overflowWrap: 'anywhere' }}>{children}</Box>
    </Box>
  );
}

/** Locale date-time for ISO metadata timestamps ('—' when missing). */
export function formatTimestamp(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(String(iso));
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}
